import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderSlot as _renderSlot, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e1388fca"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "update-form"
}
const _hoisted_2 = { class: "flex items-center q-mt-md" }
const _hoisted_3 = /*#__PURE__*/_createTextVNode("Cancel")

import { ref, computed, onMounted, getCurrentInstance, watch, toRaw, nextTick } from 'vue';
import { JsonForms } from '@jsonforms/vue';
import { quasarRenderers } from '@/shared/jsonformsQuasarRenderers/renderers';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import {
  createWorkFlow,
  getBluePrint,
  validateWorkFlow,
} from '@/pages/registrations/api/api';
import {useErrors} from '@/shared/jsonformsQuasarRenderers/util';
import { buildInitialValues, mergeEditData } from './helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkflowForm',
  props: {
    commonFormData: null,
    registrationData: null,
    workflowId: null,
    editData: null,
    cancelButton: null
  },
  setup(__props: any) {

const props = __props as {
  commonFormData: any,
  registrationData: any,
  workflowId: string,
  editData: any,
  cancelButton: () => void
}

const { proxy } = getCurrentInstance();
const { listErrors } = useErrors();

const renderers = Object.freeze([...quasarRenderers]);

const updateGeneralErrors = ref([]);
const formData = ref({});
const loadSend = ref(false);
const submitDisable = ref(true);
const loadData = ref(false);
const localSchema = ref(null);
const uiSchema = ref(null);

const lastValidatedFormData = ref<any>(null);

// While true, control-level `clearOnRefresh` watchers skip clearing dependent fields
const suppressClearOnRefresh = ref(false);



const jsonFormsConfig = computed(() => ({
  workflowId: props.workflowId,
  suppressClearOnRefresh: suppressClearOnRefresh.value,
  // When form data is hydrated from editData (e.g. duplicating a record)
  editableHydratedFields: !!props.editData,
}));

onMounted(async () => {
  proxy.$showLoading();
  try {
    const { inputVariablesSchema: schema, uiSchemas } = await getBluePrint(props.workflowId);
    delete schema.$async;
    delete schema.$schema;
    uiSchema.value = uiSchemas;
    localSchema.value = JSON.parse(JSON.stringify(schema));

    // create structure for formData based on schema and merge with editData if available
    let prepareInitialStructure = buildInitialValues(schema);
    prepareInitialStructure = mergeEditData(prepareInitialStructure, props.editData);

    // intentionally add digitalServiceApmId to formData
    const digitalServiceApmId = props.registrationData?.digitalServiceApm;
    formData.value = {...prepareInitialStructure, ...props.commonFormData, digitalServiceApmId };
  } catch (error) {
    proxy.$notify('negative', 'Error', error.message);
  } finally {
    loadData.value = true;
    proxy.$hideLoading();
  }
});

watch(() => props.editData, async (newEditData) => {
  if (!newEditData || typeof newEditData !== 'object') return;
  suppressClearOnRefresh.value = true;
  const prepareInitialStructure = buildInitialValues(localSchema.value);
  formData.value = mergeEditData(prepareInitialStructure, newEditData);
  await nextTick();
  suppressClearOnRefresh.value = false;
}, { deep: true });

function handleCancel() {
  // clearDraft();
  if (props.cancelButton) {
    props.cancelButton();
  }
}

// --- Shared: workflow validation & submit ---
async function runWorkflowValidation() {
  loadSend.value = true;
  try {
    await validateWorkFlow({
      workflowId: props.workflowId,
      inputVariables: getPlainFormData(formData.value),
    });
    updateGeneralErrors.value = [];
    submitDisable.value = false;
    lastValidatedFormData.value = formData.value;
  } catch (error) {
    if (error?.response?.data?.errors?.length) {
      updateGeneralErrors.value = [{ message: error.message }];
    } else {
      proxy.$notify('negative', 'Error', error.message);
      updateGeneralErrors.value = [{ message: error.message }];
    }
  } finally {
    loadSend.value = false;
  }
}

async function sendData() {
  try {
    loadSend.value = true;
    await createWorkFlow({
      workflowId: props.workflowId,
      inputVariables: getPlainFormData(formData.value),
    });
    proxy.$notify('positive', 'Success', uiSchema.value?.successMessage);
  } catch (error) {
    proxy.$notify('negative', 'Error', error.message);
  } finally {
    loadSend.value = false;
  }
}

const isFormValid = ref(false);

// clean up from Proxy for API requests
function getPlainFormData(data: any) {
  return JSON.parse(JSON.stringify(toRaw(data)));
}

async function validateForm({ data, errors: jsonErrors }) {
  const newData = toRaw(data);

  if (!isEqual(formData.value, newData)) {
    formData.value = newData;
  }

  isFormValid.value = !jsonErrors.length && !listErrors.value.length;

  // JSONForm validation from Schema && Third-party validation from API call and UI validation
  if (!isFormValid.value) {
    submitDisable.value = true;
    lastValidatedFormData.value = null;
  }

  debounceValidation();
}

const debounceValidation = debounce(() => {
  if (!isFormValid.value || loadSend.value) return;
  if (isEqual(lastValidatedFormData.value, formData.value)) return;
  runWorkflowValidation();
}, 2000);

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_banner = _resolveComponent("q-banner")!
  const _component_q_btn = _resolveComponent("q-btn")!

  return (loadData.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (updateGeneralErrors.value.length)
          ? (_openBlock(), _createBlock(_component_q_banner, {
              key: 0,
              class: "text-white approval-error-banner q-mb-sm"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(updateGeneralErrors.value, (error, key) => {
                  return (_openBlock(), _createElementBlock("span", {
                    class: "flex items-center",
                    key: key
                  }, [
                    _createVNode(_component_q_icon, {
                      name: "error",
                      class: "q-mr-xs",
                      size: "16px"
                    }),
                    _createTextVNode(_toDisplayString(error.message), 1)
                  ]))
                }), 128))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (localSchema.value && uiSchema.value)
          ? (_openBlock(), _createBlock(_unref(JsonForms), {
              key: 1,
              renderers: _unref(renderers),
              schema: localSchema.value,
              uischema: uiSchema.value,
              data: formData.value,
              config: _unref(jsonFormsConfig),
              validationMode: "ValidateAndShow",
              onChange: validateForm
            }, null, 8, ["renderers", "schema", "uischema", "data", "config"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_q_btn, {
            color: "primary",
            size: "md",
            class: "q-ml-sm",
            loading: loadSend.value,
            disable: loadSend.value || submitDisable.value || Boolean(updateGeneralErrors.value.length),
            style: {"height":"38px"},
            onClick: sendData,
            "no-caps": "",
            label: "Submit"
          }, null, 8, ["loading", "disable"]),
          _createVNode(_component_q_btn, {
            flat: "",
            size: "md",
            class: "btn--no-hover",
            style: {"height":"40px"},
            "no-caps": "",
            onClick: handleCancel
          }, {
            default: _withCtx(() => [
              _hoisted_3
            ]),
            _: 1
          }),
          _renderSlot(_ctx.$slots, "toolbarContent")
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})